import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { NAV_LINKS, BRAND } from "../data/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-3 group shrink-0">
          <img
            src={BRAND.logoMark}
            alt="Zyvora Digital"
            className="h-10 w-10 rounded-lg object-contain bg-white/5 p-1 group-hover:scale-105 transition"
          />
          <div className="leading-tight">
            <div className="font-display font-bold text-white text-lg tracking-tight">Zyvora</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Digital</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm font-medium rounded-full transition whitespace-nowrap ${
                  isActive ? "text-white bg-white/10" : "text-gray-300 hover:text-white hover:bg-white/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3 shrink-0">
          <Link to="/contact" data-testid="navbar-cta-button">
            <Button className="bg-gradient-brand hover:opacity-90 text-[#0A0A0A] rounded-full px-5 h-11 font-semibold group">
              Get Free Consultation
              <ArrowRight className="ml-2 h-4 w-4 transition group-hover:translate-x-0.5" />
            </Button>
          </Link>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          className="xl:hidden text-white p-2 rounded-lg hover:bg-white/10"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/10">
          <div className="px-5 py-6 space-y-1 max-h-[80vh] overflow-y-auto">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-lg text-base font-medium ${
                    isActive ? "text-white bg-white/10" : "text-gray-300 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="mobile-cta-button">
              <Button className="mt-3 w-full bg-gradient-brand text-[#0A0A0A] rounded-full h-12 font-semibold">
                Get Free Consultation
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
